import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, School, TrendingUp, Stethoscope, Pill, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';

const EarlyEnrollmentStats = () => {
    const [stats, setStats] = useState({
        studentCount: 0,
        schoolCount: 0,
        providerCount: 0,
        pharmacyCount: 0,
        loading: true
    });

    useEffect(() => {
        fetchStats();

        // Refresh stats every 30 seconds
        const interval = setInterval(fetchStats, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchStats = async () => {
        try {
            // Fetch student count
            const studentsResponse = await fetch('/api/students/count');
            if (studentsResponse.status === 429) {
                console.warn('Rate limited on student count, using cached data');
                return;
            }
            const studentsData = await studentsResponse.json();

            // Fetch school enrollment count
            const schoolsResponse = await fetch('/api/enrollments/school/count');
            if (schoolsResponse.status === 429) {
                console.warn('Rate limited on school count, using cached data');
                return;
            }
            const schoolsData = await schoolsResponse.json();

            setStats({
                studentCount: studentsData.count || 0,
                schoolCount: schoolsData.count || 0,
                providerCount: 0, // Not applicable - vertically integrated urgent care facilities
                pharmacyCount: 0, // Set to 0 for now until pharmacy signup form is ready
                loading: false
            });
        } catch (error) {
            console.error('Error fetching enrollment stats:', error);
            setStats(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <section className="section-padding bg-white">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                        Early Enrollment Progress
                    </h2>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-4">
                        See how many families and schools are already signed up for AMHAS healthcare services.
                        Join the growing community of early adopters!
                    </p>
                    <p className="text-lg text-gray-600 max-w-4xl mx-auto">
                        Every enrollment brings us closer to our goal of providing world-class healthcare to children across The Gambia. 
                        Early adopters receive priority access, guaranteed spots, and special discounts. The more families that join from 
                        your area, the sooner we can launch services in your community. Together, we're building a healthier future, one 
                        enrollment at a time.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto"
                >
                    {/* Current Students */}
                    <div className="bg-primary-50 rounded-2xl shadow-lg p-8 text-center hover:bg-primary-100 transition-all duration-300 flex flex-col h-full">
                        <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Users size={40} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Current Students
                        </h3>
                        <p className="text-gray-600 mb-6 flex-grow">
                            Early enrollments from families across The Gambia
                        </p>
                        <div className="text-5xl font-bold text-primary-600 mb-2 min-h-[4rem] flex items-center justify-center">
                            {stats.loading ? (
                                <div className="animate-pulse bg-primary-200 h-16 w-24 rounded"></div>
                            ) : (
                                <CountUp
                                    end={stats.studentCount}
                                    duration={2}
                                    separator=","
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-center space-x-2 text-primary-600">
                            <TrendingUp size={16} />
                            <span className="text-sm font-medium">Growing daily</span>
                        </div>
                    </div>

                    {/* Current Schools */}
                    <div className="bg-accent-50 rounded-2xl shadow-lg p-8 text-center hover:bg-accent-100 transition-all duration-300 flex flex-col h-full">
                        <div className="w-20 h-20 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <School size={40} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Current Schools
                        </h3>
                        <p className="text-gray-600 mb-6 flex-grow">
                            Early signups from schools ready for AMHAS services
                        </p>
                        <div className="text-5xl font-bold text-accent-600 mb-2 min-h-[4rem] flex items-center justify-center">
                            {stats.loading ? (
                                <div className="animate-pulse bg-accent-200 h-16 w-24 rounded"></div>
                            ) : (
                                <CountUp
                                    end={stats.schoolCount}
                                    duration={2}
                                    separator=","
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-center space-x-2 text-accent-600">
                            <TrendingUp size={16} />
                            <span className="text-sm font-medium">Expanding network</span>
                        </div>
                    </div>

                    {/* Current Providers */}
                    <div className="bg-secondary-50 rounded-2xl shadow-lg p-8 text-center hover:bg-secondary-100 transition-all duration-300 flex flex-col h-full">
                        <div className="w-20 h-20 bg-secondary-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Stethoscope size={40} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Urgent Care Facilities
                        </h3>
                        <p className="text-gray-600 mb-6 flex-grow">
                            AMHAS vertically integrated urgent care facilities
                        </p>
                        <div className="text-5xl font-bold text-secondary-600 mb-2 min-h-[4rem] flex items-center justify-center">
                            {stats.loading ? (
                                <div className="animate-pulse bg-secondary-200 h-16 w-24 rounded"></div>
                            ) : (
                                <CountUp
                                    end={stats.providerCount}
                                    duration={2}
                                    separator=","
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-center space-x-2 text-secondary-600">
                            <TrendingUp size={16} />
                            <span className="text-sm font-medium">Vertically integrated</span>
                        </div>
                    </div>

                    {/* Current Pharmacies */}
                    <div className="bg-gray-50 rounded-2xl shadow-lg p-8 text-center hover:bg-gray-100 transition-all duration-300 flex flex-col h-full">
                        <div className="w-20 h-20 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Pill size={40} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Provider Pharmacies
                        </h3>
                        <p className="text-gray-600 mb-6 flex-grow">
                            Pharmacies offering discounted medications to AMHAS members
                        </p>
                        <div className="text-5xl font-bold text-gray-600 mb-2 min-h-[4rem] flex items-center justify-center">
                            {stats.loading ? (
                                <div className="animate-pulse bg-gray-200 h-16 w-24 rounded"></div>
                            ) : (
                                <CountUp
                                    end={stats.pharmacyCount}
                                    duration={2}
                                    separator=","
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-center space-x-2 text-gray-600">
                            <TrendingUp size={16} />
                            <span className="text-sm font-medium">Coming soon</span>
                        </div>
                    </div>
                </motion.div>

                {/* Benefits of Early Enrollment */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto mb-8 mt-16"
                >
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Benefits of Early Enrollment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <CheckCircle size={24} className="text-white" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-2">1,200 CFA Discount</h4>
                                <p className="text-gray-700 text-sm">
                                    Early sign-ups receive a 1,200 CFA discount on the annual premium, making healthcare even more affordable.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <CheckCircle size={24} className="text-white" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-2">Guaranteed Spot</h4>
                                <p className="text-gray-700 text-sm">
                                    Secure your child's place in the program. Early enrollment ensures your family is among the first to receive services.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <CheckCircle size={24} className="text-white" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-2">Priority Launch</h4>
                                <p className="text-gray-700 text-sm">
                                    Communities with the highest enrollment get priority when we launch services. The more sign-ups from your area, 
                                    the sooner we arrive.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <CheckCircle size={24} className="text-white" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 mb-2">No Commitment Required</h4>
                                <p className="text-gray-700 text-sm">
                                    Joining the early sign-up list doesn't commit you to anything. You're simply securing your spot and helping 
                                    us plan our launch strategy.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="text-center mt-8"
                >
                    <div className="space-y-6">
                        <div className="inline-flex items-center space-x-2 bg-primary-100 rounded-full px-6 py-3">
                            <TrendingUp size={20} className="text-primary-600" />
                            <span className="text-sm font-medium text-gray-700">
                                Live counts • Updates every 30 seconds • Join the movement!
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                            <Link to="/urgent-care" className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center space-x-2">
                                <Stethoscope size={20} />
                                <span>Learn About Urgent Care Centers</span>
                            </Link>
                            <Link to="/student-enrollment" className="bg-accent-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-accent-700 transition-colors flex items-center space-x-2">
                                <Users size={20} />
                                <span>Enroll Your Child Now</span>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default EarlyEnrollmentStats;
